<template>
  <div class="qs-chat">
    <div class="qs-chat__header">
      <div class="d-flex align-items-center gap-2">
        <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
          <path d="M12 2a7 7 0 0 1 7 7v1a7 7 0 0 1-3.5 6.06V19a2 2 0 0 1-2 2h-3a2 2 0 0 1-2-2v-2.94A7 7 0 0 1 5 10V9a7 7 0 0 1 7-7z"/>
          <circle cx="9.5" cy="10" r="1" fill="currentColor" stroke="none"/>
          <circle cx="14.5" cy="10" r="1" fill="currentColor" stroke="none"/>
          <path d="M9.5 14.5c.83.83 2.17 1 3 1s2.17-.17 3-1" stroke-width="1.5"/>
        </svg>
        <span class="fw-bold text-sm">Ezer AI Assistant</span>
      </div>
      <button class="btn btn-link p-0 text-secondary" @click="clearChat" title="Clear chat">
        <i class="material-icons" style="font-size: 18px">refresh</i>
      </button>
    </div>

    <!-- Messages -->
    <div class="qs-chat__messages" ref="messagesContainer">
      <!-- Welcome message -->
      <div v-if="messages.length === 0 && !isLoading" class="qs-chat__welcome">
        <p class="text-sm text-secondary mb-2">
          👋 Hi{{ userName ? `, ${userName}` : '' }}! I'm here to help you set up your organization.
          Ask me anything about the setup process!
        </p>
        <div class="d-flex flex-wrap gap-1">
          <button
            v-for="suggestion in contextSuggestions"
            :key="suggestion"
            class="btn btn-sm btn-outline-primary mb-0 text-xs"
            @click="sendMessage(suggestion)"
          >
            {{ suggestion }}
          </button>
        </div>
      </div>

      <!-- Message bubbles -->
      <div v-for="msg in messages" :key="msg.id" :class="['qs-chat__bubble', `qs-chat__bubble--${msg.role}`]">
        <div v-if="msg.role === 'user'" class="qs-chat__user-msg">{{ msg.content }}</div>
        <div v-else-if="msg.role === 'assistant'" class="qs-chat__ai-msg" v-html="renderMarkdown(msg.content)"></div>
        <div v-if="msg.toolCalls" class="qs-chat__tools">
          <div v-for="tc in msg.toolCalls" :key="tc.id" class="text-xs text-secondary">
            <i class="material-icons" style="font-size: 12px; vertical-align: middle"
              :class="tc.status === 'success' ? 'text-success' : tc.status === 'error' ? 'text-danger' : 'text-warning'"
            >{{ tc.status === 'success' ? 'check_circle' : tc.status === 'error' ? 'error' : 'hourglass_empty' }}</i>
            {{ tc.name }}
          </div>
        </div>
      </div>

      <!-- Streaming text -->
      <div v-if="streamingText" class="qs-chat__bubble qs-chat__bubble--assistant">
        <div class="qs-chat__ai-msg" v-html="renderMarkdown(streamingText)"></div>
      </div>

      <!-- Loading indicator -->
      <div v-if="isLoading && !streamingText" class="qs-chat__typing">
        <span></span><span></span><span></span>
      </div>
    </div>

    <!-- Input -->
    <div class="qs-chat__input">
      <textarea
        ref="inputEl"
        v-model="inputText"
        rows="1"
        class="form-control form-control-sm"
        placeholder="Ask Ezer AI a question..."
        :disabled="isLoading"
        @keydown.enter.exact.prevent="sendMessage(inputText)"
      ></textarea>
      <button
        class="btn btn-sm btn-primary mb-0 ms-1"
        :disabled="!inputText.trim() || isLoading"
        @click="sendMessage(inputText)"
      >
        <i class="material-icons" style="font-size: 18px">send</i>
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, nextTick } from 'vue'
import { useStore } from 'vuex'
import { EzerChatService } from '@/services/ezer/EzerChatService'
import { EzerToolExecutor } from '@/services/ezer/EzerToolExecutor'
import { GetToken } from '@/utils/TokenHandler'
import api from '@/config/api'

const props = defineProps({
  currentStep: { type: String, default: 'activity' },
  userName: { type: String, default: '' },
})

const store = useStore()
const session = computed(() => store.getters.stSessionData || {})

const chatService = new EzerChatService(api.defaults.baseURL || '', () => GetToken())
const toolExecutor = new EzerToolExecutor(api.defaults.baseURL || '', () => GetToken())

const messages = ref([])
const streamingText = ref('')
const isLoading = ref(false)
const inputText = ref('')
const messagesContainer = ref(null)
const sessionId = ref(null)

const contextSuggestions = computed(() => {
  const stepSuggestions = {
    activity: ['What is an Activity?', 'How do I name my service?', 'What duration should I pick?'],
    workgroups: ['What is a Workgroup?', 'How many volunteers do I need?', 'What is auto-scheduling?'],
    users: ['How do I add volunteers?', 'Can I import users from a file?'],
    admins: ['What does an admin do?', 'Can I have multiple admins?'],
    review: ['What happens when I enable the activity?', 'Can I edit this later?'],
  }
  return stepSuggestions[props.currentStep] || stepSuggestions.activity
})

function renderMarkdown(text) {
  if (!text) return ''
  // Simple markdown: bold, links, line breaks
  return text
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    .replace(/\n/g, '<br>')
}

async function sendMessage(text) {
  if (!text?.trim()) return
  ensureSessionId()
  const content = text.trim()
  inputText.value = ''

  messages.value.push({
    id: Date.now() + '-' + Math.random().toString(36).substr(2, 6),
    role: 'user',
    content,
  })
  isLoading.value = true
  scrollToBottom()

  await streamConversation()
}

async function streamConversation() {
  const apiMessages = buildApiMessages()
  const pendingTools = []
  let hadError = false
  let lastUsage = null
  let lastStopReason = null
  let assistantTextForLog = ''

  await chatService.converse(
    apiMessages,
    session.value.locationid,
    'quick_setup',
    {
      onText(text) {
        streamingText.value += text
        assistantTextForLog += text
        scrollToBottom()
      },
      onToolUse(tool) { pendingTools.push(tool) },
      onMetadata(usage, stopReason) {
        if (usage) lastUsage = usage
        if (stopReason) lastStopReason = stopReason
      },
      onDone() {
        if (streamingText.value) {
          messages.value.push({
            id: Date.now() + '-' + Math.random().toString(36).substr(2, 6),
            role: 'assistant',
            content: streamingText.value,
          })
          streamingText.value = ''
        }
      },
      onError(err) {
        hadError = true
        streamingText.value = ''
        lastStopReason = 'error'
        const errorContent = `⚠ ${err.message || 'Something went wrong. Please try again.'}`
        messages.value.push({
          id: Date.now() + '-err',
          role: 'assistant',
          content: errorContent,
          isError: true,
        })
        isLoading.value = false
        logLlmCall(apiMessages, errorContent, true, lastUsage, lastStopReason)
      },
    }
  )

  if (!hadError) {
    logLlmCall(
      apiMessages,
      assistantTextForLog || (pendingTools.length ? '[tool_use]' : ''),
      false,
      lastUsage,
      lastStopReason
    )
  }
  if (!hadError && pendingTools.length > 0) {
    // Ensure an assistant message exists for tool calls
    const last = messages.value[messages.value.length - 1]
    if (!last || last.role !== 'assistant') {
      messages.value.push({ id: Date.now() + '-tc', role: 'assistant', content: '' })
    }
    const lastAssistant = messages.value[messages.value.length - 1]
    if (!lastAssistant.toolCalls) lastAssistant.toolCalls = []

    for (const tool of pendingTools) {
      lastAssistant.toolCalls.push({ name: tool.name, input: tool.input, id: tool.toolUseId, status: 'running' })
    }

    const toolResultBlocks = []
    for (const tool of pendingTools) {
      const { result, isError } = await toolExecutor.execute(
        tool.name,
        tool.input,
        session.value.locationid,
        ensureSessionId(),
      )
      const tc = lastAssistant.toolCalls.find(t => t.id === tool.toolUseId)
      if (tc) tc.status = isError ? 'error' : 'success'
      toolResultBlocks.push({ toolUseId: tool.toolUseId, content: JSON.stringify(result), isError })
    }

    messages.value.push({
      id: Date.now() + '-tr',
      role: 'tool_result',
      isToolResult: true,
      toolResults: toolResultBlocks,
    })

    await streamConversation()
  } else if (!hadError) {
    isLoading.value = false
  }

  scrollToBottom()
}

function buildApiMessages() {
  return messages.value.map(msg => {
    if (msg.role === 'user' && !msg.isToolResult) {
      return { role: 'user', content: [{ text: msg.content }] }
    }
    if (msg.role === 'assistant') {
      const blocks = []
      if (msg.content) blocks.push({ text: msg.content })
      if (msg.toolCalls?.length) {
        for (const tc of msg.toolCalls) {
          blocks.push({ toolUse: { toolUseId: tc.id, name: tc.name, input: tc.input } })
        }
      }
      return blocks.length ? { role: 'assistant', content: blocks } : null
    }
    if (msg.isToolResult) {
      const results = msg.toolResults || []
      const blocks = results.map(r => ({
        toolResult: {
          toolUseId: r.toolUseId,
          content: [{ text: typeof r.content === 'string' ? r.content : JSON.stringify(r.content) }],
          status: r.isError ? 'error' : 'success',
        }
      }))
      return { role: 'user', content: blocks }
    }
    return null
  }).filter(Boolean)
}

function clearChat() {
  messages.value = []
  streamingText.value = ''
  isLoading.value = false
  sessionId.value = null
}

function ensureSessionId() {
  if (!sessionId.value) {
    sessionId.value = Date.now().toString(36) + '-' + Math.random().toString(36).substr(2, 8)
  }
  return sessionId.value
}

function textFromContent(content) {
  if (typeof content === 'string') return content
  if (!Array.isArray(content)) return ''
  return content.map(block => {
    if (block.text) return block.text
    if (block.toolResult) return `[tool_result] ${JSON.stringify(block.toolResult.content || '')}`
    if (block.toolUse) return `[tool_use] ${block.toolUse.name || ''}`
    return ''
  }).filter(Boolean).join('\n')
}

function latestUserText(apiMessages) {
  for (let i = apiMessages.length - 1; i >= 0; i--) {
    if (apiMessages[i].role === 'user') {
      return textFromContent(apiMessages[i].content)
    }
  }
  return ''
}

function logLlmCall(apiMessages, assistantContent, isError = false, usage = null, stopReason = null) {
  const userContent = latestUserText(apiMessages)
  const entries = []
  if (userContent) entries.push({ role: 'user', content: userContent })
  entries.push({
    role: isError ? 'error' : 'assistant',
    content: assistantContent || (isError ? 'Quick Setup LLM error' : '[no text returned]'),
  })

  chatService.logConversation(
    ensureSessionId(),
    entries,
    session.value.locationid,
    'quick_setup',
    isError,
    usage,
    stopReason
  )
}
function scrollToBottom() {
  nextTick(() => {
    if (messagesContainer.value) {
      messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight
    }
  })
}
</script>

<style scoped>
.qs-chat {
  display: flex;
  flex-direction: column;
  height: 100%;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  background: #fafbfc;
  overflow: hidden;
}
.qs-chat__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.6rem 0.8rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #fff;
}
.qs-chat__header .btn-link { color: rgba(255,255,255,0.7); }
.qs-chat__header .btn-link:hover { color: #fff; }
.qs-chat__messages {
  flex: 1;
  overflow-y: auto;
  padding: 0.75rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
.qs-chat__welcome { padding: 0.5rem; }
.qs-chat__bubble { max-width: 90%; }
.qs-chat__bubble--user { align-self: flex-end; }
.qs-chat__bubble--assistant, .qs-chat__bubble--tool_result { align-self: flex-start; }
.qs-chat__user-msg {
  background: #667eea;
  color: #fff;
  border-radius: 12px 12px 2px 12px;
  padding: 0.5rem 0.75rem;
  font-size: 0.82rem;
  word-break: break-word;
}
.qs-chat__ai-msg {
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 12px 12px 12px 2px;
  padding: 0.5rem 0.75rem;
  font-size: 0.82rem;
  word-break: break-word;
}
.qs-chat__tools { padding: 0.25rem 0.5rem; }
.qs-chat__typing {
  display: flex;
  gap: 4px;
  padding: 0.5rem;
}
.qs-chat__typing span {
  width: 6px; height: 6px;
  border-radius: 50%;
  background: #667eea;
  animation: qs-bounce 1.2s infinite;
}
.qs-chat__typing span:nth-child(2) { animation-delay: 0.15s; }
.qs-chat__typing span:nth-child(3) { animation-delay: 0.3s; }
@keyframes qs-bounce {
  0%, 60%, 100% { transform: translateY(0); opacity: 0.4; }
  30% { transform: translateY(-6px); opacity: 1; }
}
.qs-chat__input {
  display: flex;
  align-items: flex-end;
  padding: 0.5rem;
  border-top: 1px solid #e9ecef;
  background: #fff;
}
.qs-chat__input textarea {
  resize: none;
  font-size: 0.82rem;
  max-height: 80px;
  border-radius: 8px;
}
</style>
